import {
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { TFunction } from "i18next"
import { FC } from "react"
import { Tenant } from "shared/tenant"

export type SstVersionFilter = "all" | "v1" | "v2"

export type LockerMetrics = {
  total: number
  v1: number
  v2: number
  virtual: number
}

type TenantOption = { id: string; label: string }

type SaLockersToolbarProps = {
  t: TFunction
  cardSx: object
  searchInput: string
  onSearchChange: (value: string) => void
  version: SstVersionFilter
  onVersionChange: (value: SstVersionFilter) => void
  tenantId: string | null
  tenants: Tenant[]
  onTenantChange: (tenantId: string | null) => void
  metrics?: LockerMetrics
  /** True total of the current (filtered) result set from the backend. */
  filteredCount: number
  isFiltered: boolean
}

const StatChip: FC<{ label: string; value: number | string; tone?: "primary" }> = ({
  label,
  value,
  tone,
}) => (
  <Stack
    direction="row"
    spacing={0.75}
    alignItems="baseline"
    sx={{
      px: 1.5,
      py: 0.75,
      borderRadius: 1.5,
      bgcolor:
        tone === "primary" ? "rgba(35, 66, 133, 0.06)" : "rgba(0,0,0,0.03)",
    }}
  >
    <Typography
      sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.7rem",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "text.secondary",
      }}
    >
      {label}
    </Typography>
  </Stack>
)

export const SaLockersToolbar: FC<SaLockersToolbarProps> = ({
  t,
  cardSx,
  searchInput,
  onSearchChange,
  version,
  onVersionChange,
  tenantId,
  tenants,
  onTenantChange,
  metrics,
  filteredCount,
  isFiltered,
}) => {
  const tenantOptions: TenantOption[] = tenants
    .map((tn) => ({ id: tn.id, label: tn.name || tn.id }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const selectedTenant =
    tenantOptions.find((o) => o.id === tenantId) ?? null

  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
      {/* Metrics row */}
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <StatChip
          label={isFiltered ? t("matching") : t("total")}
          value={
            isFiltered && metrics
              ? `${filteredCount} / ${metrics.total}`
              : (metrics?.total ?? filteredCount)
          }
          tone="primary"
        />
        {metrics && <StatChip label="SST v1" value={metrics.v1} />}
        {metrics && <StatChip label="SST v2" value={metrics.v2} />}
        {metrics && <StatChip label={t("virtual")} value={metrics.virtual} />}
      </Stack>

      {/* Controls row */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        flexWrap="wrap"
        useFlexGap
      >
        <TextField
          label={t("search")}
          placeholder={t("searchLockersPlaceholder")}
          value={searchInput}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{ minWidth: 280, flex: 1, maxWidth: 460 }}
        />

        <Autocomplete
          size="small"
          options={tenantOptions}
          value={selectedTenant}
          onChange={(_e, option) => onTenantChange(option?.id ?? null)}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          sx={{ minWidth: 240 }}
          renderInput={(inputProps) => (
            <TextField {...inputProps} label={t("organisation")} />
          )}
        />

        <ToggleButtonGroup
          size="small"
          exclusive
          value={version}
          onChange={(_e, value) => value && onVersionChange(value)}
          aria-label={t("sstVersion")}
        >
          <ToggleButton value="all">{t("all")}</ToggleButton>
          <ToggleButton value="v1">SST v1</ToggleButton>
          <ToggleButton value="v2">SST v2</ToggleButton>
        </ToggleButtonGroup>

        {isFiltered && (
          <Chip
            label={t("clearFilters")}
            variant="outlined"
            onClick={() => {
              onSearchChange("")
              onVersionChange("all")
              onTenantChange(null)
            }}
            onDelete={() => {
              onSearchChange("")
              onVersionChange("all")
              onTenantChange(null)
            }}
          />
        )}
      </Stack>
    </Box>
  )
}

export default SaLockersToolbar
